import { useRef, useState } from 'react'
import { Field } from './Inputs'
import InfoTip from './InfoTip'
import type { MulchInputs, MulchArea } from '../calculators/mulch'
import type { Locale } from '../i18n/locale'

export type WizardGoal = 'forward' | 'reverse'

interface WizardCopy {
  bagSize: string
  bagSizeTip: string
  depth: string
  depthTip: string
  bed: string
  addBed: string
  remove: string
}

interface Props {
  locale: Locale
  copy: WizardCopy
  i: MulchInputs
  set: (patch: Partial<MulchInputs>) => void
  setArea: (idx: number, patch: Partial<MulchArea>) => void
  addArea: () => void
  removeArea: (idx: number) => void
  goal: WizardGoal
  setGoal: (g: WizardGoal) => void
  onClose: () => void
}

function tr(locale: Locale, en: string, fr: string): string {
  return locale === 'fr' ? fr : en
}

function ChipButton({
  selected, onClick, children,
}: { selected: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-lg border p-4 text-left transition-colors ${
        selected
          ? 'border-brand bg-brand-tint/60 text-text'
          : 'border-border bg-surface text-text-body hover:border-brand hover:bg-surface-2'
      }`}
    >
      {children}
    </button>
  )
}

function StepDots({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: total }, (_, idx) => idx + 1).map((n) => (
        <span
          key={n}
          className={`h-1.5 w-1.5 rounded-full ${
            n === current ? 'bg-brand' : n < current ? 'bg-brand/40' : 'bg-border'
          }`}
        />
      ))}
    </div>
  )
}

export default function MulchWizard({
  locale, copy, i, set, setArea, addArea, removeArea, goal, setGoal, onClose,
}: Props) {
  const [step, setStep] = useState(1)
  // Forward path has 6 steps (Goal, Unit, Area, Depth, BagSize, Extras);
  // reverse has 5 (Goal, Unit, Bags+Size, Depth, Confirm).
  const totalSteps = goal === 'forward' ? 6 : 5
  const T = (en: string, fr: string) => tr(locale, en, fr)
  const metric = i.unit === 'metric'

  // Step 1 (Goal) and Step 2 (Unit) are single-choice — auto-advance on pick.
  // Steps with a custom-typed fallback (Depth, BagSize) ALSO auto-advance when
  // a preset chip is clicked, but keep their Next button so users typing a
  // custom value can still submit. Multi-input steps (Area, BagsHave, Extras,
  // Confirm) always require Next.
  const advanceTimer = useRef<number | null>(null)
  const cancelAdvance = () => {
    if (advanceTimer.current) {
      window.clearTimeout(advanceTimer.current)
      advanceTimer.current = null
    }
  }
  const next = () => {
    cancelAdvance()
    if (step >= totalSteps) {
      onClose()
    } else {
      setStep((s) => s + 1)
    }
  }
  const back = () => {
    cancelAdvance()
    setStep((s) => Math.max(1, s - 1))
  }
  // Apply the chip's state change immediately, then queue a quick auto-advance
  // so the user sees the selection highlight before the wizard moves on.
  const pickAndAdvance = (apply: () => void) => {
    apply()
    cancelAdvance()
    advanceTimer.current = window.setTimeout(() => {
      advanceTimer.current = null
      // Read step at fire time via the functional setStep so a stale closure
      // can't double-advance after multiple rapid clicks.
      setStep((s) => (s >= totalSteps ? s : s + 1))
      if (step >= totalSteps) onClose()
    }, 250)
  }

  // Steps where the Next button is hidden (Goal, Unit are single-choice and
  // self-progress; nothing else to do on those steps).
  const hideNext = step === 1 || step === 2

  // Step 1 — Goal. Pick forward or reverse.
  function StepGoal() {
    return (
      <div className="space-y-3">
        <h2 className="text-2xl font-semibold text-text">
          {T('What are you trying to figure out?', 'Que voulez-vous calculer?')}
        </h2>
        <p className="text-sm text-text-muted">
          {T('Both work the same way — just pick the one you need.', 'Les deux fonctionnent de la même façon — choisissez le bon.')}
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <ChipButton selected={goal === 'forward'} onClick={() => pickAndAdvance(() => setGoal('forward'))}>
            <div className="text-lg font-semibold">🛒 {T('How many bags do I need?', 'Combien de sacs me faut-il?')}</div>
            <div className="mt-1 text-xs text-text-muted">{T('I know my bed size — tell me the bag count', 'Je connais la taille — dites-moi le nombre de sacs')}</div>
          </ChipButton>
          <ChipButton selected={goal === 'reverse'} onClick={() => pickAndAdvance(() => setGoal('reverse'))}>
            <div className="text-lg font-semibold">📏 {T('How much area will my bags cover?', 'Quelle surface mes sacs couvriront-ils?')}</div>
            <div className="mt-1 text-xs text-text-muted">{T('I have bags already — tell me the coverage', 'J\'ai déjà des sacs — dites-moi la couverture')}</div>
          </ChipButton>
        </div>
      </div>
    )
  }

  // Step 2 — Unit system. Shared by both paths.
  function StepUnit() {
    return (
      <div className="space-y-3">
        <h2 className="text-2xl font-semibold text-text">
          {T('What units do you measure in?', 'Dans quelles unités mesurez-vous?')}
        </h2>
        <p className="text-sm text-text-muted">
          {T('Pick whichever feels natural — you can change this later.', 'Choisissez ce qui vous convient — modifiable plus tard.')}
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <ChipButton selected={i.unit === 'imperial'} onClick={() => pickAndAdvance(() => set({ unit: 'imperial' }))}>
            <div className="text-lg font-semibold">{T('Feet & inches', 'Pieds et pouces')}</div>
            <div className="mt-1 text-xs text-text-muted">{T('e.g., 10 × 10 ft bed, 3 in deep', 'ex.: plate-bande de 10 × 10 pi, 3 po de profondeur')}</div>
          </ChipButton>
          <ChipButton selected={i.unit === 'metric'} onClick={() => pickAndAdvance(() => set({ unit: 'metric' }))}>
            <div className="text-lg font-semibold">{T('Metres & cm', 'Mètres et cm')}</div>
            <div className="mt-1 text-xs text-text-muted">{T('e.g., 3 × 3 m bed, 7.5 cm deep', 'ex.: plate-bande de 3 × 3 m, 7,5 cm de profondeur')}</div>
          </ChipButton>
        </div>
      </div>
    )
  }

  // Forward Step 3 — Input mode + collect.
  function StepArea() {
    const lin = metric ? 'm' : (locale === 'fr' ? 'pi' : 'ft')
    const sqLabel = metric ? 'm²' : (locale === 'fr' ? 'pi²' : 'sq ft')
    return (
      <div className="space-y-3">
        <h2 className="text-2xl font-semibold text-text">
          {T('How do you want to tell me the size?', 'Comment voulez-vous indiquer la taille?')}
        </h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <ChipButton
            selected={i.inputMode === 'dimensions'}
            onClick={() => set({ inputMode: 'dimensions' })}
          >
            <div className="text-lg font-semibold">{T('Enter dimensions', 'Saisir les dimensions')}</div>
            <div className="mt-1 text-xs text-text-muted">{T('I\'ll type L × W for each bed', 'Je tape L × l pour chaque plate-bande')}</div>
          </ChipButton>
          <ChipButton
            selected={i.inputMode === 'area'}
            onClick={() => {
              if (i.inputMode === 'dimensions') {
                const sum = i.areas.reduce((s, a) => s + Math.max(0, a.length) * Math.max(0, a.width), 0)
                set({ inputMode: 'area', totalArea: Math.round(sum * 10) / 10 })
              } else {
                set({ inputMode: 'area' })
              }
            }}
          >
            <div className="text-lg font-semibold">{T('I know the total area', 'Je connais la surface totale')}</div>
            <div className="mt-1 text-xs text-text-muted">{T('Just one number', 'Un seul nombre')}</div>
          </ChipButton>
        </div>

        <div className="mt-5 rounded-lg border border-border bg-surface p-4">
          {i.inputMode === 'dimensions' ? (
            <div className="space-y-3">
              <p className="text-sm font-medium text-text-body">{T('Your beds', 'Vos plates-bandes')}</p>
              {i.areas.map((a, idx) => (
                <div key={idx} className="rounded-md border border-border p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-medium text-text-body">{copy.bed} {idx + 1}</span>
                    {i.areas.length > 1 && (
                      <button type="button" onClick={() => removeArea(idx)} className="text-xs text-danger hover:underline">{copy.remove}</button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Field label={T('Length', 'Longueur')} value={a.length} onChange={(n) => setArea(idx, { length: n })} suffix={lin} />
                    <Field label={T('Width', 'Largeur')} value={a.width} onChange={(n) => setArea(idx, { width: n })} suffix={lin} />
                  </div>
                </div>
              ))}
              <button type="button" onClick={addArea} className="w-full rounded-md border border-dashed border-border py-2 text-sm font-medium text-brand-text hover:bg-surface-2">
                {copy.addBed}
              </button>
            </div>
          ) : (
            <Field
              label={T('Total area', 'Surface totale')}
              value={i.totalArea}
              onChange={(n) => set({ totalArea: n })}
              suffix={sqLabel}
            />
          )}
        </div>
      </div>
    )
  }

  // Reverse Step 3 — How many bags + what size.
  function StepBagsHave() {
    const cuFtLabel = locale === 'fr' ? 'pi³' : 'cu ft'
    const presets = [
      { cuft: 2, label: T('2 cu ft (57 L) bags', 'Sacs de 2 pi³ (57 L)') },
      { cuft: 3, label: T('3 cu ft (85 L) bags', 'Sacs de 3 pi³ (85 L)') },
    ]
    const customSelected = !presets.some((p) => p.cuft === i.bagCuFt)
    return (
      <div className="space-y-3">
        <h2 className="text-2xl font-semibold text-text">
          {T('How many bags do you have, and what size?', 'Combien de sacs avez-vous, et de quelle taille?')}
        </h2>
        <p className="text-sm text-text-muted">
          {T('We\'ll work out how much area they cover.', 'Nous calculerons la surface qu\'ils couvrent.')}
        </p>
        <div className="rounded-lg border border-border bg-surface p-4">
          <Field
            label={T('Number of bags', 'Nombre de sacs')}
            value={i.bagsHave}
            onChange={(n) => set({ bagsHave: Math.max(1, Math.round(n)) })}
            min={1}
            step={1}
          />
        </div>

        <p className="mt-4 text-sm font-medium text-text-body">{T('Bag size', 'Taille du sac')}</p>
        <div className="grid gap-3 sm:grid-cols-2">
          {presets.map((p) => (
            <ChipButton key={p.cuft} selected={i.bagCuFt === p.cuft} onClick={() => set({ bagCuFt: p.cuft })}>
              <div className="text-base font-semibold">{p.label}</div>
            </ChipButton>
          ))}
        </div>
        <div className={`mt-3 rounded-lg border p-3 ${customSelected ? 'border-brand bg-brand-tint/60' : 'border-border bg-surface'}`}>
          <div className="mb-1 text-xs font-medium uppercase tracking-wide text-text-subtle">
            {T('Custom size', 'Taille personnalisée')}
          </div>
          <Field label="" value={i.bagCuFt} onChange={(n) => set({ bagCuFt: n })} suffix={cuFtLabel} />
        </div>
      </div>
    )
  }

  // Shared — depth presets + custom. Used by both paths.
  function StepDepth() {
    const presets = [
      { in: 2, label: T('Light · 2 in', 'Léger · 2 po'), desc: T('A refresh on an existing layer', 'Recharge d\'une couche existante') },
      { in: 3, label: T('Recommended · 3 in', 'Recommandé · 3 po'), desc: T('Sweet spot for most beds', 'Idéal pour la plupart des plates-bandes') },
      { in: 4, label: T('Heavy · 4 in', 'Épais · 4 po'), desc: T('Maximum weed suppression', 'Suppression maximale des mauvaises herbes') },
    ]
    const inLabel = locale === 'fr' ? 'po' : 'in'
    const customSelected = !presets.some((p) => p.in === i.depthIn)
    return (
      <div className="space-y-3">
        <h2 className="text-2xl font-semibold text-text">
          {T('How deep should the mulch be?', 'Quelle profondeur de paillis?')}
        </h2>
        <p className="text-sm text-text-muted">
          {T('3 inches is the most common — adjust if you have a reason to.', '3 pouces est le plus courant — ajustez au besoin.')}
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          {presets.map((p) => (
            <ChipButton key={p.in} selected={i.depthIn === p.in} onClick={() => pickAndAdvance(() => set({ depthIn: p.in }))}>
              <div className="text-base font-semibold">{p.label}</div>
              <div className="mt-1 text-xs text-text-muted">{p.desc}</div>
            </ChipButton>
          ))}
        </div>
        <div className={`mt-3 rounded-lg border p-3 ${customSelected ? 'border-brand bg-brand-tint/60' : 'border-border bg-surface'}`}>
          <div className="mb-1 text-xs font-medium uppercase tracking-wide text-text-subtle">
            {T('Custom depth — tap Next when ready', 'Profondeur personnalisée — touchez Suivant quand prêt')}
          </div>
          <Field label="" value={i.depthIn} onChange={(n) => { cancelAdvance(); set({ depthIn: n }) }} suffix={inLabel} />
        </div>
      </div>
    )
  }

  // Forward Step 5 — Bag size for buying.
  function StepBagSize() {
    const cuFtLabel = locale === 'fr' ? 'pi³' : 'cu ft'
    const presets = [
      { cuft: 2, lLabel: '57 L', label: T('Standard · 2 cu ft', 'Standard · 2 pi³'), desc: T('Most common at big-box stores', 'Le plus courant dans les grandes surfaces') },
      { cuft: 3, lLabel: '85 L', label: T('Premium · 3 cu ft', 'Premium · 3 pi³'), desc: T('Larger bags, often at Costco', 'Plus gros sacs, souvent au Costco') },
    ]
    const customSelected = !presets.some((p) => p.cuft === i.bagCuFt)
    return (
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-2xl font-semibold text-text">
            {T('What size bag are you buying?', 'Quelle taille de sac achetez-vous?')}
          </h2>
          <span className="inline-flex shrink-0 overflow-hidden rounded-md border border-border text-[11px]">
            <button type="button" onClick={() => set({ bagUnit: 'cuft' })} className={`px-1.5 py-0.5 ${i.bagUnit === 'cuft' ? 'bg-primary text-on-primary' : 'text-text-muted'}`}>{cuFtLabel}</button>
            <button type="button" onClick={() => set({ bagUnit: 'L' })} className={`px-1.5 py-0.5 ${i.bagUnit === 'L' ? 'bg-primary text-on-primary' : 'text-text-muted'}`}>L</button>
          </span>
        </div>
        <p className="text-sm text-text-muted">
          {T('Check the front of your bag — sizes are usually printed in both units.', 'Regardez l\'avant du sac — les tailles sont souvent indiquées dans les deux unités.')}
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {presets.map((p) => (
            <ChipButton key={p.cuft} selected={i.bagCuFt === p.cuft} onClick={() => pickAndAdvance(() => set({ bagCuFt: p.cuft }))}>
              <div className="text-base font-semibold">{i.bagUnit === 'L' ? p.lLabel : p.label}</div>
              <div className="mt-1 text-xs text-text-muted">{p.desc}</div>
            </ChipButton>
          ))}
        </div>
        <div className={`mt-3 rounded-lg border p-3 ${customSelected ? 'border-brand bg-brand-tint/60' : 'border-border bg-surface'}`}>
          <div className="mb-1 text-xs font-medium uppercase tracking-wide text-text-subtle">
            {T('Custom size — tap Next when ready', 'Taille personnalisée — touchez Suivant quand prêt')}
          </div>
          <Field label="" value={i.bagCuFt} onChange={(n) => { cancelAdvance(); set({ bagCuFt: n }) }} suffix={cuFtLabel} />
        </div>
      </div>
    )
  }

  // Forward Step 6 — Extras + waste.
  function StepExtras() {
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-semibold text-text">{T('Almost done — anything else?', 'Presque terminé — autre chose?')}</h2>
        <p className="text-sm text-text-muted">
          {T('These don\'t change the bag count — they add to your shopping list.', 'Ces options n\'affectent pas le nombre de sacs — elles s\'ajoutent à votre liste.')}
        </p>
        <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border bg-surface p-4 hover:bg-surface-2">
          <input
            type="checkbox"
            checked={i.needFabric}
            onChange={(e) => set({ needFabric: e.target.checked })}
            className="mt-1"
          />
          <span>
            <span className="block text-base font-semibold">{T('Add landscape fabric underneath', 'Ajouter une membrane géotextile en dessous')}</span>
            <span className="mt-1 block text-xs text-text-muted">
              {T('Helps suppress weeds long-term. Skip this in vegetable beds.', 'Aide à supprimer les mauvaises herbes à long terme. À éviter dans les potagers.')}
            </span>
          </span>
        </label>
        <div className="rounded-lg border border-border bg-surface p-4">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-text-body">
              {T('Extra allowance', 'Marge supplémentaire')}
              <InfoTip text={T('Buffer for settling and uneven beds. 10% is plenty.', 'Tampon pour le tassement et les plates-bandes inégales. 10 % suffit.')} />
            </span>
            <span className="tnum text-brand-text">{i.wastePct}%</span>
          </div>
          <input
            type="range" min={0} max={20} step={1} value={i.wastePct}
            onChange={(e) => set({ wastePct: parseInt(e.target.value) })}
            className="mt-2 w-full accent-brand"
          />
        </div>
      </div>
    )
  }

  // Reverse Step 5 — Confirm/preview before closing.
  function StepReverseConfirm() {
    return (
      <div className="space-y-3">
        <h2 className="text-2xl font-semibold text-text">{T('Ready!', 'C\'est prêt!')}</h2>
        <p className="text-sm text-text-muted">
          {T('Tap “See my result” below for how much area your bags cover — or Back to adjust.', 'Touchez « Voir mon résultat » ci-dessous pour la surface couverte — ou Précédent pour ajuster.')}
        </p>
        <div className="rounded-lg border border-brand/40 bg-brand-tint/40 p-4 text-sm">
          <p className="font-medium text-text">
            {T(
              `You said: ${i.bagsHave} bag(s) of ${i.bagCuFt} cu ft, spread ${i.depthIn} in deep.`,
              `Vous avez indiqué : ${i.bagsHave} sac(s) de ${i.bagCuFt} pi³, étalés à ${i.depthIn} po de profondeur.`,
            )}
          </p>
          <p className="mt-2 text-text-muted">
            {T(
              'If those numbers look wrong, hit Back to adjust.',
              'Si ces nombres semblent incorrects, touchez Précédent pour les ajuster.',
            )}
          </p>
        </div>
      </div>
    )
  }

  // Step routing — different sequences for forward vs reverse.
  const renderStep = () => {
    if (step === 1) return <StepGoal />
    if (step === 2) return <StepUnit />
    if (goal === 'forward') {
      if (step === 3) return <StepArea />
      if (step === 4) return <StepDepth />
      if (step === 5) return <StepBagSize />
      if (step === 6) return <StepExtras />
    } else {
      if (step === 3) return <StepBagsHave />
      if (step === 4) return <StepDepth />
      if (step === 5) return <StepReverseConfirm />
    }
    return null
  }

  return (
    <div className="rounded-xl border border-border bg-surface p-5 lg:col-span-3">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 text-xs text-text-muted">
          <span className="font-medium uppercase tracking-wide">
            {T('Guided quiz', 'Quiz guidé')} · {step}/{totalSteps}
          </span>
          <StepDots current={step} total={totalSteps} />
        </div>
        <button type="button" onClick={onClose} className="text-xs text-text-muted hover:underline">
          {T('Skip quiz', 'Quitter le quiz')}
        </button>
      </div>

      <div className="my-6">{renderStep()}</div>

      <div className="flex items-center justify-between border-t border-border pt-4">
        <button
          type="button"
          onClick={back}
          disabled={step === 1}
          className="rounded-md border border-border px-4 py-2 text-sm font-medium text-text-body hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {T('← Back', '← Précédent')}
        </button>
        {hideNext ? (
          /* Goal & Unit steps auto-advance on selection — Next would just sit unused. */
          <span className="text-xs italic text-text-subtle">
            {T('Pick to continue →', 'Choisissez pour continuer →')}
          </span>
        ) : (
          <button
            type="button"
            onClick={next}
            className="rounded-md bg-primary px-5 py-2 text-sm font-semibold text-on-primary hover:opacity-90"
          >
            {step >= totalSteps ? T('See my result →', 'Voir mon résultat →') : T('Next →', 'Suivant →')}
          </button>
        )}
      </div>
    </div>
  )
}
